firebird.factory('videoPopoutService', [
    '$rootScope',
    '$timeout',
    '$window',
    'logService',
    'helperService',
    'meetingService',
    'configService',
    'videoGlobalService',
    'executionEnvironmentEnum',
    'annotationService',
    function (
        $rootScope,
        $timeout,
        $window,
        logService,
        helperService,
        meetingService,
        configService,
        videoGlobalService,
        executionEnvironmentEnum,
        annotationService
    ) {
        const logger = logService.createLoggerWithSource('[videoroomCtrl] videoPopoutService');
        const POPOUT_VIDEO_REVEAL_TIMEOUT_MS = 3000;

        const service = {
            initialize: initialize,
            canUseLayoutPopout: canUseLayoutPopout,
            attachAnnotationToPopout: attachAnnotationToPopout,
            syncAnnotationToPopouts: syncAnnotationToPopouts,
            enableAnnotationOnPopout: enableAnnotationOnPopout,
            cleanupAnnotationOnClose: cleanupAnnotationOnClose,
            createPopoutAnnotationButton: createPopoutAnnotationButton,
            attachPopoutAnnotationButton: attachPopoutAnnotationButton,
            removePopoutAnnotationButton: removePopoutAnnotationButton
        };

        let self;
        let $scope;

        return service;

        function initialize(selfParam) {
            self = selfParam;

            if (videoGlobalService.test) {
                $window = videoGlobalService.data.$window;
            }

            $scope = videoGlobalService.data?.$scope;

            // whether to allow popout of videos
            self.layoutPopout = true;

            // layout popout feature
            self.popoutData = {
                windows: {}, // index is id or '*', and value is window object.
                width: 320,
                height: 180,
                videoTileMinWidth: 80,
                popoutMinWidth: 400,
                popoutMinHeight: 300
            };

            self.cursorCache = new Map();

            // Close all popouts when parent window is closed or refreshed
            $window.addEventListener('beforeunload', function () {
                closeAllPopouts();
            });

            self.initPopout = initPopout;
            self.popoutAddOne = popoutAddOne;
            self.popoutRemoveOne = popoutRemoveOne;
            self.onPopoutUpdateDisplayed = onPopoutUpdateDisplayed;
            self.onPopoutAllVideos = onPopoutAllVideos;
            self.onPopoutContent = onPopoutContent;
            self.onFocusPopoutWindow = onFocusPopoutWindow;
            self.onPopoutOneVideo = onPopoutOneVideo;
            self.onPopoutCreateVideo = onPopoutCreateVideo;
            self.onPopoutRemoveVideo = onPopoutRemoveVideo;
            self.onPopoutEnableVideo = onPopoutEnableVideo;
            self.onPopoutRefreshVideo = onPopoutRefreshVideo;
            self.onPopoutLeaveConf = onPopoutLeaveConf;
            self.getPopoutVideoSize = getPopoutVideoSize;

            // Annotation methods
            self.attachAnnotationToPopout = service.attachAnnotationToPopout;
            self.syncAnnotationToPopouts = service.syncAnnotationToPopouts;
            self.enableAnnotationOnPopout = service.enableAnnotationOnPopout;
            self.attachPopoutAnnotationButton = service.attachPopoutAnnotationButton;
            self.removePopoutAnnotationButton = service.removePopoutAnnotationButton;
            self.onAnnotationToggled = onAnnotationToggled;
            self.onAnnotationInSession = onAnnotationInSession;
            self.onAnnotationColorChanged = onAnnotationColorChanged;
            self.broadcastActiveContentPopoutSources = broadcastActiveContentPopoutSources;
            // Listen for annotation events
            if ($scope) {
                $scope.$on('content-popout-request', onContentPopoutRequest);
                $scope.$on('annotation-in-session', onAnnotationInSession);
                $scope.$on('annotation-color-changed', onAnnotationColorChanged);
                $scope.$on('annotation-toggled', onAnnotationToggled);
                $scope.$on('annotation-connectivity-changed', onAnnotationConnectivityChanged);
            }
        }

        function onContentPopoutRequest(e, data) {
            const windowId = data && data.windowId;
            if (windowId !== undefined && windowId !== null && windowId !== '') {
                self.onPopoutContent(true, windowId, true);
                return;
            }

            logger.warn('onContentPopoutRequest', 'Missing content popout windowId', data);
        }

        function broadcastActiveContentPopoutSources() {
            const sources = getActiveReceivingScreenVideoSources();
            $rootScope.$broadcast('active-content-popout-sources-updated', sources);
        }

        function getFileShareWindowId(attendantId) {
            if (attendantId === undefined || attendantId === null || attendantId === '') {
                return null;
            }

            if (typeof self.getScreenshareIdFromAttendantId === 'function') {
                let normalizedAttendantId = attendantId;
                if (typeof normalizedAttendantId === 'string' && normalizedAttendantId.match(/^\d+$/)) {
                    normalizedAttendantId = parseInt(normalizedAttendantId, 10);
                }

                try {
                    return String(self.getScreenshareIdFromAttendantId(normalizedAttendantId));
                } catch (error) {
                    logger.warn(
                        'getFileShareWindowId',
                        `Unable to map attendant ${attendantId} to file-share window id: ${error.message}`
                    );
                    return null;
                }
            }

            return String(attendantId);
        }

        function canPopoutFileShareForWindowId(windowId) {
            if (!self.screenshareOwner) {
                return false;
            }

            const remoteFileShareWindowId = getFileShareWindowId(self.screenshareOwner);
            const localFileShareWindowId = getFileShareWindowId(self.attendantId);

            return String(windowId) === remoteFileShareWindowId && String(windowId) !== localFileShareWindowId;
        }

        function isEligibleContentPopoutSource(contentDiv) {
            if (!contentDiv || contentDiv.length === 0) {
                return false;
            }

            const windowId = contentDiv.attr('user');
            if (!windowId) {
                return false;
            }

            if (!contentDiv.hasClass('fileplay')) {
                return true;
            }

            return canPopoutFileShareForWindowId(windowId);
        }

        function getContentPopoutPriority(contentDiv) {
            if (!contentDiv || typeof contentDiv.hasClass !== 'function') {
                return 3;
            }

            if (contentDiv.attr('spotlight') === 'true') {
                return -1;
            }

            if (contentDiv.hasClass('primary')) {
                return 0;
            }

            if (contentDiv.hasClass('secondary')) {
                return 1;
            }

            if (contentDiv.hasClass('fileplay')) {
                return 2;
            }

            return 3;
        }

        function getActiveReceivingScreenVideoSources() {
            const list = [];
            const seen = {};

            // Query all remote screen divs (including popped-out ones) to maintain stable DOM order.
            // File-share preview tiles also reuse the remote screen layout path.
            // They should only enable content popout for viewers receiving remote file share,
            // not for the presenter publishing the local file share preview.
            // Popped-out divs have enable="false" and popout="true" set on them by popoutAddOne.
            const contentDivs = $window.$('#videoroom-videos > div.remote.screen[user]');
            if (contentDivs && contentDivs.length > 0) {
                contentDivs.each(function (_, element) {
                    const contentDiv = $window.$(element);
                    const windowId = contentDiv.attr('user');
                    if (!windowId || seen[windowId] || !isEligibleContentPopoutSource(contentDiv)) {
                        return;
                    }

                    const isPopped = contentDiv.attr('popout') === 'true';
                    const isEnabled = contentDiv.attr('enable') !== 'false';

                    // Include the source if it is active (enabled) or currently popped out.
                    if (!isEnabled && !isPopped) {
                        return;
                    }

                    seen[windowId] = true;
                    list.push({
                        id: String(windowId),
                        displayName: (contentDiv.attr('displayname') || '').trim(),
                        isPopped: isPopped,
                        priority: getContentPopoutPriority(contentDiv),
                        order: list.length
                    });
                });
            }

            list.sort(function (left, right) {
                if (left.priority !== right.priority) {
                    return left.priority - right.priority;
                }

                return left.order - right.order;
            });

            return list.map(function (item) {
                return {
                    id: item.id,
                    displayName: item.displayName,
                    isPopped: item.isPopped
                };
            });
        }

        // update from config
        function initPopout() {
            self.layoutPopout = canUseLayoutPopout();

            if (!self.layoutPopout) {
                return;
            }

            broadcastActiveContentPopoutSources();
        }

        function canUseLayoutPopout() {
            const layoutPopout = configService.getValue('layoutPopout');
            if (layoutPopout == 'false') {
                return false;
            }

            // can be called before initialize(self) (e.g., from headerCtrl), so avoid relying on `self`.
            const vdiRef = self?.VDIRef || helperService.getVDIRef?.();
            if (!vdiRef || vdiRef.getVdiType() !== executionEnvironmentEnum.NonVDI) {
                return false;
            }

            if (helperService.isUniteApplication()) {
                return helperService.isUniteSupportFeature('layoutPopout');
            }

            return true;
        }

        // check if running in VDI environment
        function isVDIEnvironment() {
            return self.VDIRef && self.VDIRef.getVdiType() !== executionEnvironmentEnum.NonVDI;
        }

        function tryGetVideoSnapshot(video) {
            if (!video || !video.videoWidth || !video.videoHeight) {
                return null;
            }

            try {
                const canvas = $window.document.createElement('canvas');
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
                return canvas.toDataURL('image/jpeg', 0.8);
            } catch (error) {
                return null;
            }
        }

        function clearPopoutVideoPlaceholder(video) {
            if (!video) {
                return;
            }

            const container = video.parentNode;
            if (!container) {
                return;
            }

            video.style.opacity = '1';
            container.style.backgroundImage = '';
            container.style.backgroundPosition = '';
            container.style.backgroundRepeat = '';
            container.style.backgroundSize = '';
        }

        function clearPendingPopoutVideoReveal(video) {
            if (!video || !video._popoutRevealState) {
                return;
            }

            const revealState = video._popoutRevealState;
            const revealHandler = revealState.handler;

            try {
                if (revealHandler) {
                    video.removeEventListener('loadeddata', revealHandler);
                    video.removeEventListener('canplay', revealHandler);
                    video.removeEventListener('playing', revealHandler);
                }

                if (revealState.timeoutPromise) {
                    $timeout.cancel(revealState.timeoutPromise);
                }
            } catch (error) {
                logger.warn(
                    'clearPendingPopoutVideoReveal',
                    `Best-effort cleanup failed for popout reveal handlers: ${error.message}`
                );
            }

            delete video._popoutRevealState;
        }

        function setPopoutVideoStream(video1, video2) {
            if (!video1 || !video2) {
                return;
            }

            try {
                clearPendingPopoutVideoReveal(video2);

                const container = video2.parentNode;
                const snapshot = tryGetVideoSnapshot(video1);

                if (container && snapshot) {
                    container.style.backgroundImage = `url(${snapshot})`;
                    container.style.backgroundPosition = 'center';
                    container.style.backgroundRepeat = 'no-repeat';
                    container.style.backgroundSize = 'contain';
                    video2.style.opacity = '0';

                    const revealState = {};
                    const revealVideo = () => {
                        if (video2._popoutRevealState !== revealState) {
                            return;
                        }

                        clearPendingPopoutVideoReveal(video2);
                        clearPopoutVideoPlaceholder(video2);
                    };

                    revealState.handler = revealVideo;
                    video2._popoutRevealState = revealState;
                    video2.addEventListener('loadeddata', revealVideo);
                    video2.addEventListener('canplay', revealVideo);
                    video2.addEventListener('playing', revealVideo);
                    revealState.timeoutPromise = $timeout(revealVideo, POPOUT_VIDEO_REVEAL_TIMEOUT_MS, false);
                } else {
                    clearPopoutVideoPlaceholder(video2);
                }

                video2.srcObject = video1.srcObject;
                if (typeof video2.play === 'function') {
                    const playPromise = video2.play();
                    if (playPromise && typeof playPromise.catch === 'function') {
                        playPromise.catch(() => {});
                    }
                }

                if (video2._popoutRevealState && typeof video2.readyState === 'number' && video2.readyState >= 2) {
                    video2._popoutRevealState.handler();
                }
            } catch (error) {
                clearPendingPopoutVideoReveal(video2);
                clearPopoutVideoPlaceholder(video2);
                logger.error('setPopoutVideoStream', `Failed to update popout stream: ${error.message}`);
            }
        }

        function popoutAddOne(popout, id) {
            const video1 = $window.$('#videoroom-videos > div[user="' + id + '"] > video').get(0);
            const video2 = popout.document.querySelector('.video-container[user="' + id + '"] > video');
            setPopoutVideoStream(video1, video2);

            if (!video1 || !video2 || !video1.parentNode || !video2.parentNode) {
                logger.warn(
                    'popoutAddOne',
                    `Unable to complete popout handoff for ${id} because a video container is missing`
                );
                broadcastActiveContentPopoutSources();
                return;
            }

            const div1 = video1.parentNode,
                div2 = video2.parentNode;
            div1.setAttribute('enable', 'false');
            div1.setAttribute('popout', 'true');

            if (self.hasTestLayout) {
                const objectFit = (div1.className || '').match(/\bscreen\b/) ? 'contain' : 'cover';
                div2.style.backgroundSize = objectFit;
                div2.style.backgrounRepeat = 'no-repeat';
                div2.style.backgroundPosition = 'center';
                video2.style.display = 'none';
                const testdiv = div1.querySelector('div.testlayout');
                if (testdiv) {
                    div2.style.backgroundImage = testdiv.style.backgroundImage;
                } else {
                    // this may be called from createVideoElement,
                    // but onTestLayoutAddVideo adds testlayout afterwards.
                    $timeout(function () {
                        const testdiv = div1.querySelector('div.testlayout');
                        if (testdiv) {
                            div2.style.backgroundImage = testdiv.style.backgroundImage;
                        }
                    }, 1000);
                }
            }

            broadcastActiveContentPopoutSources();
        }

        function popoutRemoveOne(popout, id) {
            const video1 = $window.$('#videoroom-videos > div[user="' + id + '"] > video').get(0);
            if (!video1) {
                broadcastActiveContentPopoutSources();
                return;
            }
            const div1 = video1.parentNode;
            div1.setAttribute('enable', 'true');
            div1.removeAttribute('popout');

            broadcastActiveContentPopoutSources();
        }

        // called for layoutPage and layoutPopout to hide popped out videos.
        function onPopoutUpdateDisplayed(videoDivs, input, output) {
            if (!self.layoutPopout || !self.layoutPage) {
                return input.length;
            }

            for (let i = videoDivs.length - 1; i >= 0; --i) {
                const div1 = videoDivs[i];
                if (div1.getAttribute('popout')) {
                    videoDivs.splice(i, 1);
                    input.splice(i, 1);
                    if (self.layoutPageData.layout == 'allhands') {
                        output.splice(i, 1);
                    }
                    div1.setAttribute('enable', 'false');
                    div1.setAttribute(
                        'style',
                        'left: 0px; top: 0px; width: 0px; height: 0px; display: none; visibility: hidden;'
                    );
                }
            }
            return input.length;
        }

        // popout one video
        function onPopoutOneVideo(id, div) {
            if (!self.layoutPopout) {
                logger.warn('onPopoutOneVideo', 'popout feature is not enabled');
                return;
            }

            if (isVDIEnvironment()) {
                logger.warn('onPopoutOneVideo', 'popout feature is not available in VDI environment');
                return;
            }

            logger.log('onPopoutOneVideo', `Popping out video for ${id}`);
            if (self.popoutData.windows[id] === undefined) {
                const title = div.getAttribute('displayname');
                const popout = createPopoutWindow(id, title, false);
                if (!popout) {
                    logger.error('onPopoutOneVideo', 'Failed to create popout window');
                }
            } else {
                const w = self.popoutData.windows[id];
                delete self.popoutData.windows[id];
                w.close();
            }
        }

        // popout all videos
        function onPopoutAllVideos() {
            if (!self.layoutPopout) {
                logger.warn('onPopoutAllVideos', 'popout feature is not enabled');
                return;
            }

            if (isVDIEnvironment()) {
                logger.warn('onPopoutAllVideos', 'popout feature is not available in VDI environment');
                return;
            }

            logger.log('onPopoutAllVideos', 'Popping out all videos');
            if (self.popoutData.windows['*'] === undefined) {
                let divs = $window.$('#videoroom-videos > div[enable="true"]');
                if (self.layoutPage) {
                    divs = $window.$('#videoroom-videos > div[user]');
                }
                const width0 = self.popoutData.width,
                    height0 = self.popoutData.height;
                const width = width0,
                    height = $window.screen.availHeight || 720,
                    left = $window.screen.availWidth - width0;
                // 'all videos' popout uses '*' as its id, so the target name is 'native:child-*'.
                const allVideosTargetName = helperService.isElectron() ? 'native:child-*' : undefined;
                const popout = $window.open(
                    '',
                    allVideosTargetName,
                    'width=' + width + ',height=' + height + ',left=' + left
                );

                self.popoutData.windows['*'] = popout;

                const head =
                    '<title>Meeting: ' +
                    meetingService.getMeetingCode() +
                    '</title>\n' +
                    '<style type="text/css">\n' +
                    'body > div { position: absolute; left: 0px; right: 0px; top: 0px; bottom: 0px; display: flex; flex-flow: row wrap; justify-content: flex-start; align-content: flex-start; align-items: flex-start; }\n' +
                    'body > div:empty:after { content: "No videos"; position: absolute; width: 100%; height: 100%; display: flex; justify-content: center; align-items: center; font-family: sans-serif; color: grey; font-size: 20px; }\n' +
                    'div[user] { position: relative; display: inline-block; }\n' +
                    'video { width: 100%; height: 100%; background-color: black; }\n' +
                    '</style>';
                popout.document.head.innerHTML = head;

                const users = divs.toArray().map(function (div) {
                    return div.getAttribute('user');
                });
                const html =
                    '<div>' +
                    users
                        .map(function (id) {
                            const div = $window.$('#videoroom-videos > div[user="' + id + '"]').get(0);
                            const objectFit = (div.className || '').match(/\bscreen\b/) ? 'contain' : 'cover';
                            return (
                                '<div style="width: ' +
                                width0 +
                                'px; height: ' +
                                height0 +
                                'px;" user="' +
                                id +
                                '">' +
                                '<video style="object-fit: ' +
                                objectFit +
                                ';' +
                                (id == self.attendantId ? ' transform: scaleX(-1);' : '') +
                                '" autoplay muted /></div>'
                            );
                        })
                        .join('\n') +
                    '</div>';
                popout.document.body.innerHTML = html;
                users.forEach(function (id) {
                    self.popoutAddOne(popout, id);
                });
                self.updateVideosLayout();

                popout.document.body.onresize = function () {
                    const size = self.getPopoutVideoSize(popout);
                    [].slice.call(popout.document.querySelectorAll('div[user]')).forEach(function (div) {
                        div.style.width = size.width + 'px';
                        div.style.height = size.height + 'px';
                    });
                };
                popout.onbeforeunload = function () {
                    const popout = self.popoutData.windows['*'];
                    delete self.popoutData.windows['*'];
                    const users = [].slice.call(popout.document.querySelectorAll('div[user]')).map(function (div) {
                        return div.getAttribute('user');
                    });
                    users.forEach(function (id) {
                        self.popoutRemoveOne(popout, id);
                    });
                    self.updateVideosLayout();
                };
            } else {
                const w = self.popoutData.windows['*'];
                delete self.popoutData.windows['*'];
                w.close();
            }
        }

        // popout content video (screen share or received content)
        // @param {boolean|undefined} enable - if undefined, toggle; if true, open popout; if false, close popout
        // @param {string|undefined} contentId - optional specific content window id to target
        // @param {boolean|undefined} focusIfOpen - when true, focus existing popout instead of toggling/closing
        function onPopoutContent(enable, contentId, focusIfOpen) {
            if (!self.layoutPopout) {
                logger.warn('onPopoutContent', 'popout feature is not enabled');
                return;
            }

            if (isVDIEnvironment()) {
                logger.warn('onPopoutContent', 'popout feature is not available in VDI environment');
                return;
            }

            // Find content/screen share video. If id is supplied, target only that source.
            const selector = contentId
                ? '#videoroom-videos > div.remote.screen[enable="true"][user="' + contentId + '"]'
                : '#videoroom-videos > div.remote.screen[enable="true"]';
            const contentDiv = $window.$(selector).first();
            if (contentDiv.length === 0) {
                // Content div not found with enable="true" — it may already be popped out (enable="false", popout="true").
                // Check popoutData.windows for a matching screen share window and focus it instead.
                const alreadyPoppedId = contentId
                    ? self.popoutData.windows[contentId] !== undefined
                        ? contentId
                        : null
                    : Object.keys(self.popoutData.windows).find(id => {
                          if (id === '*') return false;
                          return $window.$('#videoroom-videos > div.remote.screen[user="' + id + '"]').length > 0;
                      });

                if (alreadyPoppedId !== undefined && alreadyPoppedId !== null) {
                    emitMsgflowEvent('popout:focus', alreadyPoppedId);
                    logger.log('onPopoutContent', `Content ${alreadyPoppedId} is already popped out — focusing window`);
                    onFocusPopoutWindow(alreadyPoppedId);
                } else {
                    logger.warn('onPopoutContent', 'no content video found to popout');
                }
                return;
            }

            const resolvedContentId = contentDiv.attr('user');
            if (contentDiv.hasClass('fileplay') && !canPopoutFileShareForWindowId(resolvedContentId)) {
                logger.warn(
                    'onPopoutContent',
                    `content ${resolvedContentId} is a local file share preview and cannot be popped out`
                );
                return;
            }

            const isOpen = self.popoutData.windows[resolvedContentId] !== undefined;

            // Determine action: toggle if enable is undefined, otherwise use enable value
            const shouldOpen = enable === undefined ? !isOpen : enable;

            if (shouldOpen && !isOpen) {
                logger.log('onPopoutContent', `Opening content popout for ${resolvedContentId}`);
                const title = contentDiv.attr('displayname');
                const popout = createPopoutWindow(resolvedContentId, title, true);
                if (!popout) {
                    emitMsgflowEvent('popout:error', resolvedContentId, { reason: 'open' });
                    logger.error('onPopoutContent', 'Failed to create content popout window');
                } else {
                    emitMsgflowEvent('popout:open', resolvedContentId);
                }
            } else if (shouldOpen && isOpen && focusIfOpen) {
                emitMsgflowEvent('popout:focus', resolvedContentId);
                logger.log('onPopoutContent', `Focusing existing content popout for ${resolvedContentId}`);
                onFocusPopoutWindow(resolvedContentId);
            } else if (!shouldOpen && isOpen) {
                logger.log('onPopoutContent', `Closing content popout for ${resolvedContentId}`);
                const w = self.popoutData.windows[resolvedContentId];
                w._msgflowCloseReason = 'toggle';
                delete self.popoutData.windows[resolvedContentId];
                w.close();
            } else {
                logger.log('onPopoutContent', `Popout already in desired state (${shouldOpen ? 'open' : 'closed'})`);
            }
        }

        // Focus on active popout window
        // @param {string|undefined} windowId - Optional ID of specific window to focus (user ID for individual video, presenter's ID for content share, or '*' for all-videos window). If not provided, focuses the first available window.
        function onFocusPopoutWindow(windowId) {
            if (!self.layoutPopout) {
                logger.warn('onFocusPopoutWindow', 'popout feature is not enabled');
                return;
            }

            if (isVDIEnvironment()) {
                logger.warn('onFocusPopoutWindow', 'popout feature is not available in VDI environment');
                return;
            }

            // Get all open popout windows
            const windowKeys = Object.keys(self.popoutData.windows);

            if (windowKeys.length === 0) {
                logger.log('onFocusPopoutWindow', 'No popout windows are currently open');
                return;
            }

            // Determine which window to focus
            let targetKey;
            if (windowId !== undefined && self.popoutData.windows[windowId] !== undefined) {
                // Focus specific window if ID provided and exists
                targetKey = windowId;
                logger.log('onFocusPopoutWindow', `Targeting specific window: ${windowId}`);
            } else if (windowId !== undefined) {
                // Requested window doesn't exist
                logger.warn(
                    'onFocusPopoutWindow',
                    `Window with ID '${windowId}' not found. Available windows: ${windowKeys.join(', ')}`
                );
                return;
            } else {
                // No ID provided, focus first available window
                targetKey = windowKeys[0];
                logger.log('onFocusPopoutWindow', `No window ID provided, focusing first window: ${targetKey}`);
            }

            const popoutWindow = self.popoutData.windows[targetKey];

            if (popoutWindow && !popoutWindow.closed) {
                try {
                    // Use IPC to focus the window in Electron, fallback to direct focus for browsers
                    const isElectron = helperService.isElectron();
                    if (isElectron && popoutWindow.preloadedApi?.remote?.ipcRenderer?.sendFocus) {
                        popoutWindow.preloadedApi.remote.ipcRenderer.sendFocus();
                        logger.log('onFocusPopoutWindow', `Sent IPC focus command for popout window: ${targetKey}`);
                    } else {
                        popoutWindow.focus();
                        logger.log('onFocusPopoutWindow', `Focused on popout window (browser): ${targetKey}`);
                    }
                } catch (error) {
                    logger.error('onFocusPopoutWindow', `Failed to focus window: ${error.message}`);
                }
            } else {
                logger.warn('onFocusPopoutWindow', 'Popout window is closed or invalid');
            }
        }

        // video is added or enabled.
        function onPopoutCreateVideo(id, div, created) {
            if (!self.layoutPopout) {
                logger.warn('onPopoutCreateVideo', 'popout feature is not enabled');
                return;
            }

            if (div.getAttribute('enable') != 'true') {
                logger.log('onPopoutCreateVideo', 'Video not enabled, skipping popout check');
                return;
            }

            if (self.popoutData.windows['*'] !== undefined) {
                const popout = self.popoutData.windows['*'];
                const existingDiv = popout.document.querySelector('.video-container[user="' + id + '"]');

                if (existingDiv) {
                    // Video already exists in popout, refresh the stream
                    logger.log('onPopoutCreateVideo', `Refreshing existing popout video for ${id}`);
                    self.popoutAddOne(popout, id);
                } else {
                    // Create new video in popout
                    logger.log('onPopoutCreateVideo', `Creating popout for ${id}`);
                    const div0 = popout.document.createElement('div');
                    const size = self.getPopoutVideoSize(popout);
                    div0.setAttribute('style', 'width: ' + size.width + 'px; height: ' + size.height + 'px;');
                    div0.setAttribute('user', id);
                    const objectFit = (div.className || '').match(/\bscreen\b/) ? 'contain' : 'cover';
                    div0.innerHTML =
                        '<video style="object-fit: ' +
                        objectFit +
                        ';' +
                        (id == self.attendantId ? ' transform: scaleX(-1);' : '') +
                        '" autoplay muted />';
                    popout.document.querySelector('body > div').appendChild(div0);
                    self.popoutAddOne(popout, id);
                }
            }
        }

        function getPopoutVideoSize(popout) {
            const ratio = 16 / 9;
            let width;
            if (popout.innerWidth > 640) {
                const count = Math.floor(popout.innerWidth / self.popoutData.width);
                width = Math.floor(popout.innerWidth / count);
            } else {
                width = Math.max(self.popoutData.videoTileMinWidth, popout.innerWidth);
            }
            const height = Math.round(width / ratio);
            return { width: width, height: height };
        }

        // video is removed or disabled.
        function onPopoutRemoveVideo(id, div) {
            if (!self.layoutPopout) {
                logger.warn('onPopoutRemoveVideo', 'popout feature is not enabled');
                return;
            }

            if (self.popoutData.windows[id] !== undefined) {
                logger.log('onPopoutRemoveVideo', `Removing popout for ${id}`);
                // this will cause the external popout to close on reconnection/republish.
                const w = self.popoutData.windows[id];
                delete self.popoutData.windows[id];
                w.close();
            } else if (self.popoutData.windows['*'] !== undefined) {
                logger.log('onPopoutRemoveVideo', `Removing ${id} from all videos popout`);
                const popout = self.popoutData.windows['*'];
                const div0 = popout.document.querySelector('.video-container[user="' + id + '"]');
                if (div0) {
                    div0.parentNode.removeChild(div0);
                }
            }
        }

        function onPopoutEnableVideo(id, div, enable) {
            if (enable) {
                self.onPopoutCreateVideo(id, div, false);
            } else {
                self.onPopoutRemoveVideo(id, div);
            }
        }

        // refresh video stream in popout window (for when stream is restarted/republished)
        function onPopoutRefreshVideo(id, div) {
            if (!self.layoutPopout) {
                logger.warn('onPopoutRefreshVideo', 'popout feature is not enabled');
                return;
            }

            if (!id || !div) {
                logger.warn('onPopoutRefreshVideo', 'missing id or div parameter');
                return;
            }

            // Check if this video is currently popped out
            if (self.popoutData.windows[id] !== undefined) {
                // Single video popout - refresh the stream
                logger.log('onPopoutRefreshVideo', `Refreshing popout stream for ${id}`);
                const video1 = $window.$('#videoroom-videos > div[user="' + id + '"] > video').get(0);
                if (video1 && video1.srcObject) {
                    const popoutWindow = self.popoutData.windows[id];
                    const video2 = popoutWindow.document.querySelector('video');
                    if (video2) {
                        setPopoutVideoStream(video1, video2);
                    }
                }
            } else if (self.popoutData.windows['*'] !== undefined) {
                // All videos popout - refresh the specific stream
                logger.log('onPopoutRefreshVideo', `Refreshing stream for ${id} in all videos window`);
                const video1 = $window.$('#videoroom-videos > div[user="' + id + '"] > video').get(0);
                if (video1 && video1.srcObject) {
                    const popout = self.popoutData.windows['*'];
                    const video2 = popout.document.querySelector('.video-container[user="' + id + '"] > video');
                    if (video2) {
                        setPopoutVideoStream(video1, video2);
                    }
                }
            }
        }

        // leave conf, close all external windows.
        function onPopoutLeaveConf() {
            closeAllPopouts();
        }

        function closeAllPopouts() {
            if (self.layoutPopout) {
                for (const id in self.popoutData.windows) {
                    const win = self.popoutData.windows[id];
                    if (win && !win.closed) {
                        try {
                            logger.log('closeAllPopouts', `Force closing popout window for ${id}`);

                            // Cleanup annotation resources before closing (only for content popouts, not all-videos)
                            if (id !== '*') {
                                service.cleanupAnnotationOnClose(win, id);
                            }

                            delete self.popoutData.windows[id];
                            win.close();
                        } catch (error) {
                            logger.error('closeAllPopouts', `Error closing popout ${id}: ${error.message}`);
                            delete self.popoutData.windows[id];
                        }
                    } else if (win && win.closed) {
                        // Clean up reference to already closed window
                        delete self.popoutData.windows[id];
                    }
                }
            }
        }

        // Handle annotation toggle events for popout windows
        function onAnnotationToggled(evt, toggled) {
            const popout = getActiveContentPopout();
            if (!popout) return;

            const { id, popoutWindow } = popout;

            if (toggled) {
                if (!getPopoutCanvas(popoutWindow)) {
                    service.attachAnnotationToPopout(popoutWindow, id);
                }
                service.enableAnnotationOnPopout(id, true);
            } else {
                const canvas = getPopoutCanvas(popoutWindow);
                resetPopoutAnnotationSession(canvas);
                service.enableAnnotationOnPopout(id, false);
            }

            // Update popout annotation button state
            const annotationButton = popoutWindow.document.getElementById('popout-annotation-button');
            if (annotationButton) {
                syncPopoutButtonState(popoutWindow, annotationButton);
            }
        }

        // Handle annotation session state changes for popout windows
        function onAnnotationInSession(evt, inSession) {
            const popout = getActiveContentPopout();
            if (!popout) return;

            const { id, popoutWindow } = popout;

            if (inSession) {
                if (!getPopoutCanvas(popoutWindow)) {
                    service.attachAnnotationToPopout(popoutWindow, id);
                }
                service.enableAnnotationOnPopout(id, shouldEnablePopoutAnnotationCanvas());
            } else {
                const canvas = getPopoutCanvas(popoutWindow);
                if (canvas) {
                    resetPopoutAnnotationSession(canvas);
                }
                service.enableAnnotationOnPopout(id, shouldEnablePopoutAnnotationCanvas());
            }

            // Update popout annotation button state
            const annotationButton = popoutWindow.document.getElementById('popout-annotation-button');
            if (annotationButton) {
                syncPopoutButtonState(popoutWindow, annotationButton);
            }
        }

        // Handle annotation color change events for popout windows
        function onAnnotationColorChanged(evt, colorData) {
            const newColor = colorData.color || colorData;

            const popout = getActiveContentPopout();
            if (!popout) return;

            const { popoutWindow } = popout;

            // Update canvas cursor if annotation is active
            const canvas = getPopoutCanvas(popoutWindow);
            const state = annotationService.getAnnotationButtonState();
            if (canvas && state.shouldAllowInteraction) {
                canvas.style.cursor = getCachedCursor(newColor);
            }

            annotationService.updateAuxiliaryAnnotationColor(canvas, newColor);
            annotationService.refreshAuxiliaryAnnotationCanvas(canvas);

            // Update button state (includes color update)
            const annotationButton = popoutWindow.document.getElementById('popout-annotation-button');
            if (annotationButton) {
                syncPopoutButtonState(popoutWindow, annotationButton);
            }
        }

        // Handle annotation connectivity state changes for popout windows
        function onAnnotationConnectivityChanged(evt, connectivityState) {
            const popout = getActiveContentPopout();
            if (!popout) return;

            const canvas = getPopoutCanvas(popout.popoutWindow);
            if (canvas) {
                syncPopoutCanvasState(canvas, popout.popoutWindow, shouldEnablePopoutAnnotationCanvas());
            }

            const annotationButton = popout.popoutWindow.document.getElementById('popout-annotation-button');
            if (annotationButton) {
                syncPopoutButtonState(popout.popoutWindow, annotationButton);
            }
        }

        // Annotation Support - Content/screen share popouts only
        // NOTE: Only ONE content window supports annotations at a time

        function isValidContentPopout(id, popoutWindow) {
            return id !== '*' && popoutWindow && !popoutWindow.closed;
        }

        function isSecondaryScreen(id) {
            // Check if the video element in the parent window has the 'secondary' class
            const videoDiv = $window.$('#videoroom-videos > div[user="' + id + '"]');
            return videoDiv.hasClass('secondary');
        }

        function getContentDivForId(id) {
            return $window.$('#videoroom-videos > div[user="' + id + '"]');
        }

        function getContentDisplayName(id) {
            const contentDiv = getContentDivForId(id);
            if (!contentDiv || typeof contentDiv.attr !== 'function') {
                return '';
            }

            return (contentDiv.attr('displayname') || '').trim();
        }

        function getSourceContentType(id) {
            const contentDiv = getContentDivForId(id);
            if (!contentDiv || typeof contentDiv.hasClass !== 'function') {
                return 'video';
            }

            if (contentDiv.hasClass('fileplay')) {
                return 'fileplay';
            }

            if (contentDiv.hasClass('screen')) {
                return 'screen';
            }

            return 'video';
        }

        function getPopoutContentType(id, popoutWindow) {
            const popoutVideoContainer = popoutWindow?.document?.querySelector(`.video-container[user="${id}"]`);
            const contentType = popoutVideoContainer?.getAttribute('data-content-type');

            return contentType || getSourceContentType(id);
        }

        function isFileShareContentPopout(id, popoutWindow) {
            return getPopoutContentType(id, popoutWindow) === 'fileplay';
        }

        function isScreenContentPopout(id, popoutWindow) {
            return getPopoutContentType(id, popoutWindow) === 'screen';
        }

        function canAnnotateContentPopout(id, popoutWindow) {
            return (
                isValidContentPopout(id, popoutWindow) &&
                isScreenContentPopout(id, popoutWindow) &&
                !isSecondaryScreen(id) &&
                !isFileShareContentPopout(id, popoutWindow)
            );
        }

        function getPopoutCanvas(popoutWindow) {
            return popoutWindow?.document?.getElementById('annotation-canvas');
        }

        function emitMsgflowEvent(eventName, id, extraData) {
            if (!self || typeof self.msgflowEvent !== 'function' || !eventName || !id) {
                return;
            }

            self.msgflowEvent(
                eventName,
                Object.assign(
                    {
                        id: String(id),
                        title: getContentDisplayName(id)
                    },
                    extraData || {}
                )
            );
        }

        function shouldMonitorPopoutAnnotation(enable, state) {
            return Boolean(enable && state && !state.isDisabled && (state.isActive || state.isEnabled));
        }

        function shouldEnablePopoutAnnotationCanvas() {
            return annotationService.getIsLocalAnnotationEnabled() || annotationService.getIsInSession();
        }

        function syncPopoutCanvasState(canvas, popoutWindow, enable) {
            if (!canvas || !popoutWindow || popoutWindow.closed) {
                return;
            }

            const state = annotationService.getAnnotationButtonState();

            canvas.style.display = enable ? 'block' : 'none';
            canvas.style.pointerEvents = enable && state.shouldAllowInteraction ? 'auto' : 'none';
            canvas.style.cursor =
                enable && state.shouldAllowInteraction ? getCachedCursor(annotationService.getPenColor()) : 'default';

            if (!enable || !state.shouldAllowInteraction) {
                resetPopoutAnnotationSession(canvas);
            }

            if (shouldMonitorPopoutAnnotation(enable, state)) {
                startPopoutAnnotationMonitor(canvas, canvas._video, popoutWindow);
                updateCanvasToVideo(canvas, canvas._video, popoutWindow);
            } else {
                stopPopoutAnnotationMonitor(canvas);
            }
        }

        function resetPopoutAnnotationSession(canvas) {
            if (!canvas) {
                return;
            }

            if (annotationService.clearAuxiliaryAnnotationCanvas) {
                annotationService.clearAuxiliaryAnnotationCanvas(canvas);
            }
        }

        function getPopoutAnnotationMonitorState(video) {
            if (!video) {
                return null;
            }

            return [video.clientWidth, video.clientHeight, video.videoWidth || 0, video.videoHeight || 0].join(':');
        }

        function stopPopoutAnnotationMonitor(canvas) {
            if (!canvas) {
                return;
            }

            const monitorWindow = canvas._annotationMonitorWindow;
            const monitorId = canvas._annotationMonitorId;

            if (monitorWindow && monitorId !== undefined && monitorId !== null) {
                monitorWindow.clearInterval(monitorId);
            }

            delete canvas._annotationMonitorId;
            delete canvas._annotationMonitorState;
            delete canvas._annotationMonitorWindow;
        }

        function startPopoutAnnotationMonitor(canvas, video, popoutWindow) {
            if (
                !canvas ||
                !video ||
                !popoutWindow ||
                popoutWindow.closed ||
                canvas._annotationMonitorId !== undefined
            ) {
                return;
            }

            const syncIfNeeded = force => {
                if (
                    popoutWindow.closed ||
                    !canvas.isConnected ||
                    !video.isConnected ||
                    canvas.ownerDocument !== popoutWindow.document
                ) {
                    stopPopoutAnnotationMonitor(canvas);
                    return;
                }

                const nextState = getPopoutAnnotationMonitorState(video);
                if (!force && nextState === canvas._annotationMonitorState) {
                    return;
                }

                canvas._annotationMonitorState = nextState;
                updateCanvasToVideo(canvas, video, popoutWindow);
            };

            canvas._annotationMonitorWindow = popoutWindow;
            canvas._annotationMonitorState = null;
            canvas._annotationMonitorId = popoutWindow.setInterval(() => syncIfNeeded(false), 100);
            syncIfNeeded(true);
        }

        // Get the single active content popout window (since only one screenshare supports annotations)
        function getActiveContentPopout() {
            for (const id in self.popoutData.windows) {
                const popoutWindow = self.popoutData.windows[id];
                // Only annotatable primary screen popouts are eligible here.
                if (canAnnotateContentPopout(id, popoutWindow)) {
                    return { id, popoutWindow };
                }
            }
            return null;
        }

        function getCachedCursor(color) {
            if (!self.cursorCache.has(color)) {
                const colorCode = color.replace('#', '');
                self.cursorCache.set(
                    color,
                    `url("data:image/svg+xml,%3Csvg width='25' height='24' viewBox='0 0 25 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20.1272 7.5962C20.713 7.01041 20.713 6.06066 20.1272 5.47488L18.713 4.06066C18.1272 3.47488 17.1775 3.47488 16.5917 4.06066L15.531 5.12132L19.0666 8.65686L20.1272 7.5962Z' fill='%23${colorCode}'/%3E%3Cpath d='M17.6523 10.0711L14.1168 6.53554L4.15234 16.5L3.65234 20.5L7.65234 20L17.6523 10.0711Z' fill='%23${colorCode}'/%3E%3C/svg%3E%0A") 0 30, crosshair`.replace(
                        /\$\{colorCode\}/g,
                        colorCode
                    )
                );
            }
            return self.cursorCache.get(color);
        }

        function getPopoutStylesheetHref() {
            const parentStylesheet = $window.document.getElementById('app-level-redesign');
            const href = parentStylesheet && parentStylesheet.getAttribute('href');

            return href || 'dist/app-redesign.min.css';
        }

        function escapeHtml(value) {
            return String(value || '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        }

        function getPopoutAnnotationButtonBounds(annotationButton, popoutWindow, margin = 20) {
            const hasTitleBar = popoutWindow.document.body.classList.contains('with-titlebar');
            const titleBarHeight = hasTitleBar ? 32 : 0;
            const buttonWidth = annotationButton?.offsetWidth || 72;
            const buttonHeight = annotationButton?.offsetHeight || 72;
            return {
                minLeft: margin,
                minTop: titleBarHeight + margin,
                maxLeft: Math.max(margin, popoutWindow.innerWidth - buttonWidth - margin),
                maxTop: Math.max(titleBarHeight + margin, popoutWindow.innerHeight - buttonHeight - margin)
            };
        }

        function getPopoutAnnotationButtonDragBounds(annotationButton, popoutWindow) {
            const hasTitleBar = popoutWindow.document.body.classList.contains('with-titlebar');
            const titleBarHeight = hasTitleBar ? 32 : 0;
            return {
                minLeft: 0,
                minTop: titleBarHeight,
                maxLeft: Math.max(0, popoutWindow.innerWidth - annotationButton.offsetWidth),
                maxTop: Math.max(titleBarHeight, popoutWindow.innerHeight - annotationButton.offsetHeight)
            };
        }

        function setPopoutAnnotationButtonPosition(annotationButton, popoutWindow, left, top, margin = 20) {
            if (!annotationButton || !popoutWindow || popoutWindow.closed) {
                return;
            }

            const bounds = getPopoutAnnotationButtonBounds(annotationButton, popoutWindow, margin);
            const clampedLeft = Math.min(Math.max(left, bounds.minLeft), bounds.maxLeft);
            const clampedTop = Math.min(Math.max(top, bounds.minTop), bounds.maxTop);

            popoutWindow.$(annotationButton).css({
                left: clampedLeft + 'px',
                top: clampedTop + 'px',
                right: 'auto',
                bottom: 'auto'
            });
        }

        function clampPopoutAnnotationButton(annotationButton, popoutWindow, margin = 20) {
            if (!annotationButton || !popoutWindow || popoutWindow.closed) {
                return;
            }

            const rect = annotationButton.getBoundingClientRect();
            setPopoutAnnotationButtonPosition(annotationButton, popoutWindow, rect.left, rect.top, margin);
        }

        function resetPopoutAnnotationButtonLocation(annotationButton, popoutWindow, margin = 20) {
            if (!annotationButton || !popoutWindow || popoutWindow.closed) {
                return;
            }

            const bounds = getPopoutAnnotationButtonBounds(annotationButton, popoutWindow, margin);
            setPopoutAnnotationButtonPosition(annotationButton, popoutWindow, bounds.maxLeft, bounds.maxTop, margin);
            annotationButton._hasExplicitPosition = false;
        }

        function createPopoutWindow(id, title, isContent = false) {
            const width = self.popoutData.width * 3;
            const height = self.popoutData.height * 3;
            const minWidth = self.popoutData.popoutMinWidth;
            const minHeight = self.popoutData.popoutMinHeight;
            const availW = $window.screen.availWidth || 1280;
            const availH = $window.screen.availHeight || 720;

            // Alternate placement: even-indexed popouts → top-left, odd → bottom-right.
            // Each successive pair is offset by an additional 40px cascade so windows don't overlap.
            const existingCount = Object.keys(self.popoutData.windows).filter(k => k !== '*').length;
            const padding = 60;
            const cascade = Math.floor(existingCount / 2) * 40;
            let left, top;
            if (existingCount % 2 === 0) {
                // top-left corner
                left = padding + cascade;
                top = padding + cascade;
            } else {
                // bottom-right corner
                left = availW - width - padding - cascade;
                top = availH - height - padding - cascade;
            }

            const isElectron = helperService.isElectron();

            // Use a unique target name per popout id so that window.open does not
            // return an existing window reference when a second popout is opened.
            // The Electron setNewWindowHandler checks startsWith('native:child') to
            // allow any suffixed variant through as a native window.
            const windowTargetName = isElectron ? `native:child-${id}` : undefined;
            const popout = $window.open(
                '',
                windowTargetName,
                `width=${width},height=${height},left=${left},top=${top}`
            );

            if (!popout) {
                logger.error('createPopoutWindow', 'Failed to create popout window - window.open returned null');
                if (helperService.isElectron()) {
                    self.showAlert('Error', 'Failed to open popout window. Please restart the meeting.');
                } else {
                    self.showAlert('Error', 'Failed to open popout window. Please check if popups are blocked.');
                }
                return null;
            }

            logger.log(
                'createPopoutWindow',
                `Setting up resize constraints for popout ${id}, minWidth=${minWidth}, minHeight=${minHeight}`
            );

            // Copy jQuery from parent window to popout
            popout.$ = $window.$;
            popout.jQuery = $window.jQuery;

            const stylesheetHref = getPopoutStylesheetHref();
            const fallbackStyle = popout.document.createElement('style');
            fallbackStyle.textContent = `
                html {
                    background: #000;
                }
                body.videoroom-popout {
                    margin: 0;
                    padding: 0;
                    overflow: hidden;
                    height: 100vh;
                    min-width: ${minWidth}px;
                    min-height: ${minHeight}px;
                    background: #000;
                }
                body.videoroom-popout .title-bar {
                    display: flex;
                    height: 32px;
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    z-index: 99999;
                    -webkit-app-region: drag;
                }
                body.videoroom-popout .title-bar .btn-container {
                    display: flex;
                    align-items: center;
                    -webkit-app-region: no-drag;
                }
                body.videoroom-popout .title-bar .win-btn {
                    width: 40px;
                    height: 31px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                body.videoroom-popout .video-container {
                    position: absolute;
                    inset: 0;
                }
                body.videoroom-popout.with-titlebar .video-container {
                    top: 32px;
                }
                body.videoroom-popout .video-container > video {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    display: block;
                    background: #000;
                }
            `;

            const titleElement = popout.document.createElement('title');
            titleElement.textContent = title;
            const safeTitle = escapeHtml(title);

            const stylesheetLink = popout.document.createElement('link');
            stylesheetLink.rel = 'stylesheet';
            stylesheetLink.href = stylesheetHref;

            popout.document.head.innerHTML = '';
            popout.document.head.appendChild(titleElement);
            popout.document.head.appendChild(fallbackStyle);
            popout.document.head.appendChild(stylesheetLink);

            const shouldRenderCustomTitleBar = isElectron;
            const shouldShowWindowActions = isElectron && helperService.isWindows();
            const titleBarButtonsHtml = shouldShowWindowActions
                ? `<button type="button" class="uni-button uni-button_icon uni-button_tertiary uni-button_neutral uni-button_tiny win-btn" id="popout-minimize-btn">
                    <svg viewBox="0 0 16 16">
                        <rect x="1" y="13" width="14" height="2" />
                    </svg>
                    </button>
                    <button type="button" class="uni-button uni-button_icon uni-button_tertiary uni-button_neutral uni-button_tiny win-btn" id="popout-maximize-btn">
                        <svg viewBox="0 0 16 16">
                            <path d="M14,15H2a1,1,0,0,1-1-1V2A1,1,0,0,1,2,1H14a1,1,0,0,1,1,1V14A1,1,0,0,1,14,15ZM3,13H13V3H3Z" />
                        </svg>
                    </button>
                    <button type="button" class="uni-button uni-button_icon uni-button_tertiary uni-button_neutral uni-button_tiny win-btn hidden" id="popout-restore-btn">
                        <svg viewBox="0 0 16 16">
                            <path d="M14.9,0H4.1c-0.6,0-1,0.4-1,1L3,3H1.1c-0.6,0-1,0.4-1,1v10.9c0,0.6,0.4,1,1,1h10.8c0.6,0,1-0.4,1-1v-2h2 c0.6,0,1-0.4,1-1V1C15.9,0.5,15.5,0,14.9,0z M11,14H2V5h9V14z M14,11h-1.1V4c0-0.6-0.4-1-1-1H5V2h9V11z" />
                        </svg>
                    </button>
                    <button type="button" class="uni-button uni-button_icon uni-button_tertiary uni-button_neutral uni-button_tiny win-btn win-btn-close" id="popout-close-btn">
                        <svg viewBox="0 0 16 16">
                            <polygon points="14.71 2.71 13.29 1.29 8 6.59 2.71 1.29 1.29 2.71 6.59 8 1.29 13.29 2.71 14.71 8 9.41 13.29 14.71 14.71 13.29 9.41 8 14.71 2.71" />
                        </svg>
                    </button>`
                : '';

            const titleBarHtml = shouldRenderCustomTitleBar
                ? `<div class="title-bar">
                    <div class="title">${safeTitle}</div>
                    <div class="btn-container">
                        ${titleBarButtonsHtml}
                    </div>
                </div>`
                : '';

            const contentType = getSourceContentType(id);
            const videoStyle = id === self.attendantId ? 'transform: scaleX(-1);' : '';
            const bodyClasses = shouldRenderCustomTitleBar ? 'videoroom-popout with-titlebar' : 'videoroom-popout';
            popout.document.body.className = bodyClasses;
            popout.document.body.innerHTML = `${titleBarHtml}<div class="video-container" user="${id}" data-content-type="${contentType}"><video style="${videoStyle}" autoplay muted /></div>`;

            // Setup title bar button handlers
            if (shouldRenderCustomTitleBar) {
                if (shouldShowWindowActions) {
                    const closeBtn = popout.document.getElementById('popout-close-btn');
                    const minimizeBtn = popout.document.getElementById('popout-minimize-btn');
                    const maximizeBtn = popout.document.getElementById('popout-maximize-btn');
                    const restoreBtn = popout.document.getElementById('popout-restore-btn');

                    if (closeBtn) {
                        closeBtn.addEventListener('click', () => {
                            popout.close();
                        });
                    }

                    const clearPreloadedApiRetry = () => {
                        if (popout._preloadedApiRetryTimeoutId === undefined) {
                            return;
                        }

                        $window.clearTimeout(popout._preloadedApiRetryTimeoutId);
                        delete popout._preloadedApiRetryTimeoutId;
                    };

                    // Wait for preloadedApi to be available (preload script loads asynchronously)
                    const waitForPreloadedApi = (callback, attempt = 0) => {
                        if (popout.closed) {
                            clearPreloadedApiRetry();
                            return;
                        }

                        if (popout.preloadedApi?.remote?.ipcRenderer) {
                            clearPreloadedApiRetry();
                            logger.log('createPopoutWindow', `preloadedApi available on attempt ${attempt + 1}`);
                            callback();
                        } else if (attempt < 20) {
                            // Try for 2 seconds (20 * 100ms)
                            logger.log('createPopoutWindow', `Waiting for preloadedApi, attempt ${attempt + 1}`);
                            popout._preloadedApiRetryTimeoutId = $window.setTimeout(
                                () => waitForPreloadedApi(callback, attempt + 1),
                                100
                            );
                        } else {
                            clearPreloadedApiRetry();
                            logger.error(
                                'createPopoutWindow',
                                'preloadedApi never became available - hiding non-functional buttons'
                            );
                            // Hide minimize/maximize/restore buttons (they need IPC), but keep close button (works without IPC)
                            if (minimizeBtn) minimizeBtn.style.display = 'none';
                            if (maximizeBtn) maximizeBtn.style.display = 'none';
                            if (restoreBtn) restoreBtn.style.display = 'none';
                        }
                    };

                    waitForPreloadedApi(() => {
                        logger.log('createPopoutWindow', 'Initializing button handlers with preloadedApi');

                        if (minimizeBtn) {
                            minimizeBtn.addEventListener('click', () => {
                                logger.log('createPopoutWindow', 'Minimize button clicked');
                                popout.preloadedApi.remote.ipcRenderer.sendMinimize();
                            });
                        }

                        if (maximizeBtn && restoreBtn) {
                            maximizeBtn.addEventListener('click', () => {
                                logger.log('createPopoutWindow', 'Maximize button clicked');
                                popout.preloadedApi.remote.ipcRenderer.sendMaximize();
                            });

                            restoreBtn.addEventListener('click', () => {
                                logger.log('createPopoutWindow', 'Restore button clicked');
                                popout.preloadedApi.remote.ipcRenderer.sendUnmaximize();
                            });

                            // Initialize button state
                            popout.preloadedApi.remote.ipcRenderer
                                .isMaximized()
                                .then(isMaximized => {
                                    logger.log('createPopoutWindow', `Initial maximized state: ${isMaximized}`);
                                    if (isMaximized) {
                                        maximizeBtn.classList.add('hidden');
                                        restoreBtn.classList.remove('hidden');
                                    } else {
                                        maximizeBtn.classList.remove('hidden');
                                        restoreBtn.classList.add('hidden');
                                    }
                                })
                                .catch(error => {
                                    logger.error('createPopoutWindow', `Failed to get initial state: ${error}`);
                                });

                            // Check window state on resize (similar to Angular title-bar component)
                            const checkWindowState = () => {
                                popout.preloadedApi.remote.ipcRenderer.isMaximized().then(isMaximized => {
                                    logger.log('createPopoutWindow', `Window state checked: ${isMaximized}`);
                                    if (isMaximized) {
                                        maximizeBtn.classList.add('hidden');
                                        restoreBtn.classList.remove('hidden');
                                    } else {
                                        maximizeBtn.classList.remove('hidden');
                                        restoreBtn.classList.add('hidden');
                                    }
                                });
                            };

                            popout.addEventListener('resize', checkWindowState);
                            logger.log('createPopoutWindow', 'Window resize listener attached');
                        }
                    });
                }
            }

            self.popoutData.windows[id] = popout;
            self.popoutAddOne(popout, id);
            self.updateVideosLayout();

            // Setup annotation support for content popouts (only for primary screens)
            if (isContent && annotationService.getIsAnnotationEnabled() && canAnnotateContentPopout(id, popout)) {
                service.attachAnnotationToPopout(popout, id);
                service.attachPopoutAnnotationButton(popout, id);
                if (annotationService.getIsInSession()) {
                    service.enableAnnotationOnPopout(id, true);
                }
            }

            popout.onbeforeunload = function () {
                if (isContent) {
                    emitMsgflowEvent('popout:close', id, {
                        reason: popout._msgflowCloseReason || 'window'
                    });
                }

                if (popout._preloadedApiRetryTimeoutId !== undefined) {
                    $window.clearTimeout(popout._preloadedApiRetryTimeoutId);
                    delete popout._preloadedApiRetryTimeoutId;
                }

                if (isContent) {
                    service.cleanupAnnotationOnClose(popout, id);
                    service.removePopoutAnnotationButton(popout, id);
                }
                delete self.popoutData.windows[id];
                self.popoutRemoveOne(popout, id);
                self.updateVideosLayout();
            };

            return popout;
        }

        function calculateVideoContentArea(video) {
            if (!video.videoWidth || !video.videoHeight) {
                return {
                    width: video.clientWidth,
                    height: video.clientHeight,
                    offsetX: 0,
                    offsetY: 0
                };
            }

            const videoAspectRatio = video.videoWidth / video.videoHeight;
            const containerAspectRatio = video.clientWidth / video.clientHeight;

            if (videoAspectRatio > containerAspectRatio) {
                const renderedHeight = video.clientWidth / videoAspectRatio;
                return {
                    width: video.clientWidth,
                    height: renderedHeight,
                    offsetX: 0,
                    offsetY: (video.clientHeight - renderedHeight) / 2
                };
            } else {
                const renderedWidth = video.clientHeight * videoAspectRatio;
                return {
                    width: renderedWidth,
                    height: video.clientHeight,
                    offsetX: (video.clientWidth - renderedWidth) / 2,
                    offsetY: 0
                };
            }
        }

        function updateCanvasToVideo(canvas, video, popoutWindow) {
            if (!canvas || !video) return;

            const area = calculateVideoContentArea(video);
            const width = Math.round(area.width);
            const height = Math.round(area.height);
            const offsetX = Math.round(area.offsetX);
            const offsetY = Math.round(area.offsetY);
            const nextState = `${width}:${height}:${offsetX}:${offsetY}`;
            if (canvas._lastCanvasArea === nextState) {
                return;
            }

            canvas._lastCanvasArea = nextState;

            canvas.width = width;
            canvas.height = height;
            canvas.style.width = width + 'px';
            canvas.style.height = height + 'px';
            canvas.style.left = offsetX + 'px';
            canvas.style.top = offsetY + 'px';

            annotationService.clearAuxiliaryAnnotationCanvas(canvas);
        }

        function attachAnnotationToPopout(popoutWindow, id) {
            if (!popoutWindow || !annotationService.getIsAnnotationEnabled()) {
                logger.warn('attachAnnotationToPopout', `Invalid popout or annotation disabled for ${id}`);
                return;
            }

            // Skip annotation for secondary screens - only primary screen supports annotation
            if (!canAnnotateContentPopout(id, popoutWindow)) {
                return;
            }

            try {
                const video = popoutWindow.document.querySelector(`.video-container[user="${id}"] > video`);
                if (!video) {
                    logger.warn('attachAnnotationToPopout', `Video element not found for ${id}`);
                    return;
                }

                if (getPopoutCanvas(popoutWindow)) {
                    return;
                }

                const videoDiv = video.parentElement;
                if (popoutWindow.getComputedStyle(videoDiv).position === 'static') {
                    videoDiv.style.position = 'relative';
                }

                const canvas = popoutWindow.document.createElement('canvas');
                canvas.id = 'annotation-canvas';
                canvas.style.cssText = 'position:absolute;pointer-events:none;z-index:1000;display:none';
                videoDiv.appendChild(canvas);

                const updateHandler = () => updateCanvasToVideo(canvas, video, popoutWindow);
                const windowResizeHandler = () => updateCanvasToVideo(canvas, video, popoutWindow);

                ['loadedmetadata', 'resize'].forEach(event => {
                    video.addEventListener(event, updateHandler);
                });
                popoutWindow.addEventListener('resize', windowResizeHandler);

                updateHandler();

                canvas._updateHandler = updateHandler;
                canvas._windowResizeHandler = windowResizeHandler;
                canvas._popoutWindow = popoutWindow;
                canvas._video = video;

                annotationService.setupAuxiliaryAnnotationCanvas(canvas, popoutWindow);
                setupAnnotationEventHandlers(canvas);
            } catch (error) {
                emitMsgflowEvent('annotation:error', id, { scope: 'popout', reason: 'attach' });
                logger.error('attachAnnotationToPopout', `Failed to attach annotation for ${id}: ${error.message}`);
            }
        }

        function syncAnnotationToPopouts(annotationData) {
            if (!self.layoutPopout) return;

            const popout = getActiveContentPopout();
            if (popout && popout.popoutWindow.handleAnnotationData) {
                popout.popoutWindow.handleAnnotationData(annotationData);
            }
        }

        function enableAnnotationOnPopout(id, enable) {
            if (!self.layoutPopout || id === '*') return;

            const popoutWindow = self.popoutData.windows[id];
            if (!canAnnotateContentPopout(id, popoutWindow)) {
                return;
            }

            const canvas = getPopoutCanvas(popoutWindow);
            if (!canvas) {
                logger.warn('enableAnnotationOnPopout', `Canvas not found for ${id}`);
                return;
            }

            syncPopoutCanvasState(canvas, popoutWindow, enable);
        }

        function cleanupAnnotationOnClose(popoutWindow, id) {
            if (!isValidContentPopout(id, popoutWindow)) return;

            try {
                const canvas = getPopoutCanvas(popoutWindow);
                if (canvas) {
                    removeAnnotationEventHandlers(canvas);
                    canvas.remove();
                }
            } catch (error) {
                logger.error('cleanupAnnotationOnClose', `Cleanup failed for ${id}: ${error.message}`);
            }
        }

        function setupAnnotationEventHandlers(canvas) {
            const sendMouseEvent = (event, eventType) => {
                const data = getCanvasMouseData(event, canvas);
                data.mouseEvent = true;
                data[eventType] = true;

                if (annotationService.getIsInSession()) {
                    annotationService.dataChannelSend(data);
                }

                return data;
            };

            const handlers = {
                mousedown: event => {
                    if (!annotationService.getIsInSession()) {
                        return;
                    }
                    const data = sendMouseEvent(event, 'mouseDown');

                    if (canvas._annotationLocalEchoState) {
                        annotationService.handleAuxiliaryAnnotationMouseDown(canvas, {
                            x: data.clientX,
                            y: data.clientY
                        });
                    } else {
                        logger.warn(
                            'setupAnnotationEventHandlers',
                            'No local echo state available on popout mousedown'
                        );
                    }
                },
                mouseup: event => {
                    if (!annotationService.getIsInSession()) {
                        return;
                    }
                    const data = sendMouseEvent(event, 'mouseUp');

                    if (canvas._annotationLocalEchoState) {
                        annotationService.handleAuxiliaryAnnotationMouseUp(canvas, {
                            x: data.clientX,
                            y: data.clientY
                        });
                    } else {
                        logger.warn('setupAnnotationEventHandlers', 'No local echo state available on popout mouseup');
                    }
                },
                mousemove: event => {
                    if (annotationService.getIsInSession() && event.buttons > 0) {
                        const data = getCanvasMouseData(event, canvas);
                        Object.assign(data, { mouseEvent: true, mouseMove: true });
                        annotationService.dataChannelSend(data);

                        if (canvas._annotationLocalEchoState) {
                            annotationService.handleAuxiliaryAnnotationMouseMove(canvas, {
                                x: data.clientX,
                                y: data.clientY
                            });
                        } else {
                            logger.warn(
                                'setupAnnotationEventHandlers',
                                'No local echo state available on popout mousemove'
                            );
                        }
                    }
                }
            };

            Object.entries(handlers).forEach(([event, handler]) => {
                canvas.addEventListener(event, handler);
            });
            canvas.addEventListener('contextmenu', handlers.mouseup);
            canvas._annotationHandlers = handlers;
        }

        function removeAnnotationEventHandlers(canvas) {
            if (!canvas) return;

            stopPopoutAnnotationMonitor(canvas);
            resetPopoutAnnotationSession(canvas);
            annotationService.teardownAuxiliaryAnnotationCanvas(canvas);
            delete canvas._lastCanvasArea;

            if (canvas._annotationHandlers) {
                Object.entries(canvas._annotationHandlers).forEach(([event, handler]) => {
                    canvas.removeEventListener(event, handler);
                });
                canvas.removeEventListener('contextmenu', canvas._annotationHandlers.mouseup);
                delete canvas._annotationHandlers;
            }

            if (canvas._updateHandler && canvas._video) {
                ['loadedmetadata', 'resize'].forEach(event => {
                    canvas._video.removeEventListener(event, canvas._updateHandler);
                });
                delete canvas._updateHandler;
                delete canvas._video;
            }

            if (canvas._windowResizeHandler && canvas._popoutWindow) {
                canvas._popoutWindow.removeEventListener('resize', canvas._windowResizeHandler);
                delete canvas._windowResizeHandler;
                delete canvas._popoutWindow;
            }
        }

        function getCanvasMouseData(event, canvas) {
            const rect = canvas.getBoundingClientRect();
            const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
            const y = Math.max(0, Math.min(event.clientY - rect.top, rect.height));

            return {
                clientX: x,
                clientY: y,
                left: x,
                top: y,
                width: rect.width,
                height: rect.height,
                canvasWidth: rect.width,
                canvasHeight: rect.height
            };
        }

        // Popout Annotation Button Management
        function createPopoutAnnotationButton(popoutWindow, id) {
            if (!annotationService.getIsAnnotationEnabled() || !canAnnotateContentPopout(id, popoutWindow)) {
                return null;
            }

            const annotationButton = popoutWindow.document.createElement('div');
            annotationButton.id = 'popout-annotation-button';
            annotationButton.className = 'annotation-enabler-button animated';

            // Position at bottom-right corner with proper margins (72px button + 20px margin)
            annotationButton.style.position = 'fixed';
            annotationButton.style.right = '20px';
            annotationButton.style.bottom = '20px';
            annotationButton.style.left = 'auto';
            annotationButton.style.top = 'auto';

            const penContainer = popoutWindow.document.createElement('div');
            penContainer.className = 'pen-container';

            const penIcon = popoutWindow.document.createElement('i');
            penIcon.className = 'pen';
            penContainer.appendChild(penIcon);

            const loadingIcon = popoutWindow.document.createElement('i');
            loadingIcon.className = 'loading';
            loadingIcon.style.display = 'none';
            loadingIcon.innerHTML = `
                <div class="spinner">
                    <div class="bounce1"></div>
                    <div class="bounce2"></div>
                    <div class="bounce3"></div>
                </div>
            `;
            penContainer.appendChild(loadingIcon);

            const colorPicker = popoutWindow.document.createElement('div');
            colorPicker.id = 'annotation-color-picker';
            colorPicker.className = 'color-picker';

            const colorGrid = popoutWindow.document.createElement('div');
            colorGrid.className = 'color-picker-grid';

            const colors = annotationService.getColors();

            colors.forEach(color => {
                const colorBtn = popoutWindow.document.createElement('div');
                colorBtn.className = 'color-btn';
                colorBtn.style.backgroundColor = color;
                colorBtn.setAttribute('data-color', color);

                const input = popoutWindow.document.createElement('input');
                input.type = 'radio';
                input.name = 'am-color-btn';
                input.value = color;

                colorBtn.appendChild(input);
                colorGrid.appendChild(colorBtn);
            });

            colorPicker.appendChild(colorGrid);

            const colorChosen = popoutWindow.document.createElement('div');
            colorChosen.className = 'color-chosen';

            const tooltip = popoutWindow.document.createElement('div');
            tooltip.className = 'annotation-tooltip hidden';
            tooltip.textContent = 'Click to turn annotation on';

            annotationButton.appendChild(penContainer);
            annotationButton.appendChild(colorPicker);
            annotationButton.appendChild(colorChosen);
            annotationButton.appendChild(tooltip);

            return annotationButton;
        }

        function attachPopoutAnnotationButton(popoutWindow, id) {
            if (!canAnnotateContentPopout(id, popoutWindow)) {
                return;
            }

            try {
                const annotationButton = createPopoutAnnotationButton(popoutWindow, id);
                if (!annotationButton) {
                    return;
                }

                attachPopoutButtonEventListeners(annotationButton, popoutWindow, id);

                popoutWindow.document.body.appendChild(annotationButton);
                resetPopoutAnnotationButtonLocation(annotationButton, popoutWindow);

                // Track if button was dragged to prevent click event
                let wasDragged = false;

                // Make button draggable using jQuery UI
                popoutWindow.$(annotationButton).draggable({
                    start: function (event, ui) {
                        wasDragged = false;
                        const tooltip = annotationButton.querySelector('.annotation-tooltip');
                        if (tooltip) {
                            tooltip.classList.add('hidden');
                        }
                        hidePopoutColorPicker(annotationButton, 0);
                    },
                    drag: function (event, ui) {
                        wasDragged = true;
                        annotationButton._hasExplicitPosition = true;
                        const tooltip = annotationButton.querySelector('.annotation-tooltip');
                        if (tooltip) {
                            tooltip.classList.add('hidden');
                        }
                        hidePopoutColorPicker(annotationButton, 0);
                        const dragBounds = getPopoutAnnotationButtonDragBounds(annotationButton, popoutWindow);
                        ui.position.left = Math.min(Math.max(ui.position.left, dragBounds.minLeft), dragBounds.maxLeft);
                        ui.position.top = Math.min(Math.max(ui.position.top, dragBounds.minTop), dragBounds.maxTop);
                    },
                    stop: function (event, ui) {
                        annotationButton._hasExplicitPosition = true;
                        clampPopoutAnnotationButton(annotationButton, popoutWindow);

                        // Reset drag flag after a short delay to prevent click
                        setTimeout(() => {
                            wasDragged = false;
                        }, 1);
                    }
                });

                const resizeHandler = () => {
                    if (annotationButton._hasExplicitPosition) {
                        clampPopoutAnnotationButton(annotationButton, popoutWindow);
                    } else {
                        resetPopoutAnnotationButtonLocation(annotationButton, popoutWindow);
                    }
                };

                popoutWindow.addEventListener('resize', resizeHandler);
                annotationButton._resizeHandler = resizeHandler;

                // Store reference to drag state for click handler
                annotationButton._isDragging = () => wasDragged;

                hideMainAnnotationButton();
                syncPopoutButtonState(popoutWindow, annotationButton);

                const mainButton = $window.$('#annotation-button')[0];
                if (mainButton) {
                    if (mainButton.classList.contains('bounce')) {
                        annotationButton.classList.add('bounce');
                        if (mainButton.classList.contains('infinite')) {
                            annotationButton.classList.add('infinite');
                        }
                    }

                    const currentColor = annotationService.getPenColor();
                    if (currentColor) {
                        updatePopoutButtonColor(annotationButton, currentColor);
                        const colorInput = annotationButton.querySelector(`input[value="${currentColor}"]`);
                        if (colorInput) {
                            colorInput.checked = true;
                        }
                    }
                }
            } catch (error) {
                logger.error('attachPopoutAnnotationButton', error);
            }
        }

        function removePopoutAnnotationButton(popoutWindow, id) {
            if (!popoutWindow || popoutWindow.closed) {
                showMainAnnotationButton();
                return;
            }

            const annotationButton = popoutWindow.document.getElementById('popout-annotation-button');
            if (annotationButton) {
                // Destroy jQuery UI draggable
                if (popoutWindow.$ && popoutWindow.$(annotationButton).draggable('instance')) {
                    popoutWindow.$(annotationButton).draggable('destroy');
                }

                if (annotationButton._resizeHandler) {
                    popoutWindow.removeEventListener('resize', annotationButton._resizeHandler);
                    delete annotationButton._resizeHandler;
                }

                removePopoutButtonEventListeners(annotationButton);
                annotationButton.remove();
            }

            showMainAnnotationButton();
        }

        function hideMainAnnotationButton() {
            const mainButton = $window.$('#annotation-button')[0];
            if (mainButton && !mainButton.hasAttribute('data-hidden-for-popout')) {
                const originalDisplay = mainButton.style.display || '';
                mainButton.setAttribute('data-original-display', originalDisplay);
                mainButton.style.display = 'none';
                mainButton.setAttribute('data-hidden-for-popout', 'true');
            }

            annotationService.setHasActiveContentPopout(true);

            $timeout(() => {
                if ($scope) {
                    $scope.$digest();
                }
            }, 0);
        }

        function showMainAnnotationButton() {
            const mainButton = $window.$('#annotation-button')[0];
            if (mainButton) {
                if (mainButton.hasAttribute('data-hidden-for-popout')) {
                    const originalDisplay = mainButton.getAttribute('data-original-display') || '';
                    mainButton.style.display = originalDisplay;
                    mainButton.removeAttribute('data-hidden-for-popout');
                    mainButton.removeAttribute('data-original-display');
                } else if (mainButton.style.display === 'none') {
                    mainButton.style.display = '';
                }

                mainButton.style.removeProperty('pointer-events');
                mainButton.style.removeProperty('opacity');
            }

            annotationService.setHasActiveContentPopout(false);

            $timeout(() => {
                if ($scope && !$scope.$$phase) {
                    try {
                        $scope.$digest();
                    } catch (digestError) {
                        $scope.$evalAsync();
                    }
                }

                if (mainButton && $window.$ && $window.$().tooltip) {
                    $window.$(mainButton).tooltip('fixTitle');
                }
            }, 50);
        }

        function attachPopoutButtonEventListeners(button, popoutWindow, id) {
            const clickHandler = event => {
                if (event.target.closest('#annotation-color-picker')) {
                    return;
                }

                // Check if button was just dragged
                if (button._isDragging && button._isDragging()) {
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }

                event.preventDefault();
                event.stopPropagation();

                if (!annotationService.shouldAllowAnnotationInteraction()) {
                    return;
                }

                toggleAnnotationFromPopout();
            };

            const colorPicker = button.querySelector('#annotation-color-picker');
            const colorButtons = button.querySelectorAll('#annotation-color-picker .color-btn');
            const colorChangeHandlers = [];

            const colorPickerClickHandler = event => {
                event.stopPropagation();
                event.preventDefault();
            };
            colorPicker.addEventListener('click', colorPickerClickHandler);

            const colorPickerEnterHandler = () => {
                if (button._hideColorPickerTimeout) {
                    clearTimeout(button._hideColorPickerTimeout);
                    button._hideColorPickerTimeout = null;
                }
            };
            const colorPickerLeaveHandler = () => {
                hidePopoutColorPicker(button, 300);
            };
            colorPicker.addEventListener('mouseenter', colorPickerEnterHandler);
            colorPicker.addEventListener('mouseleave', colorPickerLeaveHandler);

            colorButtons.forEach(colorBtn => {
                const handler = event => {
                    event.stopPropagation();
                    event.preventDefault();

                    const newColor = colorBtn.getAttribute('data-color');

                    colorButtons.forEach(btn => btn.classList.remove('selected'));
                    colorBtn.classList.add('selected');

                    const input = colorBtn.querySelector('input[type="radio"]');
                    if (input) {
                        input.checked = true;
                    }

                    annotationService.onColorChange(newColor);
                    updatePopoutButtonColor(button, newColor);
                    hidePopoutColorPicker(button, 100);
                };

                colorBtn.addEventListener('click', handler);
                colorChangeHandlers.push({ element: colorBtn, handler });
            });

            const mouseEnterHandler = event => {
                if (!event.which && !button._isDragging()) {
                    showPopoutColorPicker(button);

                    const tooltip = button.querySelector('.annotation-tooltip');
                    if (tooltip) {
                        tooltip.classList.remove('hidden');
                        updatePopoutButtonTooltip(button);
                    }
                }
            };

            const mouseLeaveHandler = () => {
                button._hideColorPickerTimeout = setTimeout(() => {
                    hidePopoutColorPicker(button, 0);
                }, 200);

                const tooltip = button.querySelector('.annotation-tooltip');
                if (tooltip) {
                    tooltip.classList.add('hidden');
                }
            };

            const mouseDownHandler = event => {
                if (event.target.closest('#annotation-color-picker')) {
                    return;
                }

                hidePopoutColorPicker(button, 0);

                const tooltip = button.querySelector('.annotation-tooltip');
                if (tooltip) {
                    tooltip.classList.add('hidden');
                }
            };

            button.addEventListener('click', clickHandler);
            button.addEventListener('mouseenter', mouseEnterHandler);
            button.addEventListener('mouseleave', mouseLeaveHandler);
            button.addEventListener('mousedown', mouseDownHandler);

            button._popoutEventHandlers = {
                click: clickHandler,
                mouseenter: mouseEnterHandler,
                mouseleave: mouseLeaveHandler,
                mousedown: mouseDownHandler,
                colorChangeHandlers: colorChangeHandlers,
                colorPickerClick: colorPickerClickHandler,
                colorPickerEnter: colorPickerEnterHandler,
                colorPickerLeave: colorPickerLeaveHandler
            };
        }

        function removePopoutButtonEventListeners(button) {
            if (!button._popoutEventHandlers) {
                return;
            }

            const handlers = button._popoutEventHandlers;

            button.removeEventListener('click', handlers.click);
            button.removeEventListener('mouseenter', handlers.mouseenter);
            button.removeEventListener('mouseleave', handlers.mouseleave);
            button.removeEventListener('mousedown', handlers.mousedown);

            if (button._hideColorPickerTimeout) {
                clearTimeout(button._hideColorPickerTimeout);
            }

            const colorPicker = button.querySelector('#annotation-color-picker');
            if (colorPicker && handlers.colorPickerClick) {
                colorPicker.removeEventListener('click', handlers.colorPickerClick);
                colorPicker.removeEventListener('mouseenter', handlers.colorPickerEnter);
                colorPicker.removeEventListener('mouseleave', handlers.colorPickerLeave);
            }

            handlers.colorChangeHandlers.forEach(({ element, handler }) => {
                element.removeEventListener('click', handler);
            });

            delete button._popoutEventHandlers;
        }

        function syncPopoutButtonState(popoutWindow, button) {
            if (!button || !annotationService) {
                return;
            }

            const state = annotationService.getAnnotationButtonState();

            button.classList.toggle('disabled', state.isDisabled);
            button.classList.toggle('inactive', !state.isActive);

            // Update pen icon visibility and color
            updatePenIconState(button, state);

            // Update color chosen indicator visibility and color
            updateColorChosenIndicator(button, state);

            // Update color picker availability
            updateColorPickerAvailability(button, state);

            updateButtonInteractionState(button, state);
            updateColorSelection(button, state.currentColor);
            updatePopoutButtonTooltip(button);
        }

        function updatePenIconState(button, state) {
            const penIcon = button.querySelector('.pen');
            const loadingIcon = button.querySelector('.loading');
            if (penIcon && loadingIcon) {
                if (state.isConnecting) {
                    penIcon.style.display = 'none';
                    loadingIcon.style.display = 'block';
                } else {
                    penIcon.style.display = 'block';
                    loadingIcon.style.display = 'none';
                }

                penIcon.style.background = !state.isDisabled && state.isConnected ? 'white' : '#73767d';
            }
        }

        function updateColorChosenIndicator(button, state) {
            const colorChosen = button.querySelector('.color-chosen');
            if (colorChosen) {
                if (state.isActive) {
                    colorChosen.style.visibility = 'visible';
                    updatePopoutButtonColor(button, state.currentColor);
                } else {
                    colorChosen.style.visibility = 'hidden';
                }
            }
        }

        function updateColorPickerAvailability(button, state) {
            const colorPicker = button.querySelector('#annotation-color-picker');
            if (colorPicker && !state.isActive) {
                colorPicker.style.display = 'none';
                colorPicker.style.visibility = 'hidden';
                colorPicker.style.opacity = '0';
            }
        }

        function updateButtonInteractionState(button, state) {
            if (state.isDisabled) {
                button.style.opacity = '0.8';
            } else {
                button.style.opacity = '0.95';
            }

            // Always keep cursor as pointer
            button.style.cursor = state.isDisabled ? 'default' : 'pointer';
        }

        function updateColorSelection(button, selectedColor) {
            const colorButtons = button.querySelectorAll('.color-btn');
            colorButtons.forEach(btn => {
                const btnColor = btn.getAttribute('data-color');
                const isSelected = btnColor === selectedColor;
                btn.classList.toggle('selected', isSelected);

                const input = btn.querySelector('input[type="radio"]');
                if (input) {
                    input.checked = isSelected;
                }
            });
        }

        function updatePopoutButtonColor(button, color) {
            const colorChosenElement = button.querySelector('.color-chosen');
            if (colorChosenElement) {
                colorChosenElement.style.backgroundColor = color;
                colorChosenElement.style.border = color === '#000000' ? '1px solid #4F535B' : '0px';
                colorChosenElement.style.boxSizing = color === '#000000' ? 'content-box' : 'inherit';
            }
        }

        function updatePopoutButtonTooltip(button) {
            const tooltip = button.querySelector('.annotation-tooltip');
            if (!tooltip) return;

            const tooltipText = annotationService.getAnnotationStatusTooltip();
            tooltip.textContent = tooltipText;

            if (!tooltipText) {
                tooltip.classList.add('hidden');
                return;
            }

            // Position tooltip dynamically based on available space.
            // Visibility is controlled by hover handlers, not by state sync.
            const buttonRect = button.getBoundingClientRect();
            const popoutWindow = button.ownerDocument.defaultView;
            const windowWidth = popoutWindow.innerWidth;
            const spaceLeft = buttonRect.left;
            const spaceRight = windowWidth - buttonRect.right;
            const minSpaceRequired = 200; // Minimum space needed for tooltip

            tooltip.classList.remove('position-left', 'position-right');

            // Prefer left, only switch to right if insufficient space on left
            if (spaceLeft >= minSpaceRequired) {
                tooltip.classList.add('position-left');
            } else {
                tooltip.classList.add('position-right');
            }
        }

        function showPopoutColorPicker(button) {
            const colorPicker = button.querySelector('#annotation-color-picker');

            const state = annotationService.getAnnotationButtonState();
            const shouldShow = state.isActive;

            if (colorPicker && shouldShow) {
                const buttonRect = button.getBoundingClientRect();
                const popoutWindow = button.ownerDocument.defaultView;
                const windowHeight = popoutWindow.innerHeight;
                const spaceAbove = buttonRect.top;
                const spaceBelow = windowHeight - buttonRect.bottom;

                // Temporarily show to measure actual height
                colorPicker.style.display = 'block';
                colorPicker.style.visibility = 'hidden';
                const colorPickerHeight = colorPicker.offsetHeight || 200;
                colorPicker.style.visibility = 'visible';

                colorPicker.classList.remove('position-above', 'position-below');

                // Choose position based on available space
                if (spaceAbove >= colorPickerHeight || spaceAbove > spaceBelow) {
                    colorPicker.classList.add('position-above');
                } else {
                    colorPicker.classList.add('position-below');
                }

                colorPicker.style.opacity = '0.9';
            }
        }

        function hidePopoutColorPicker(button, delay = 0) {
            const colorPicker = button.querySelector('#annotation-color-picker');
            if (colorPicker) {
                if (delay > 0) {
                    setTimeout(() => {
                        colorPicker.style.display = 'none';
                        colorPicker.style.visibility = 'hidden';
                        colorPicker.style.opacity = '0';
                    }, delay);
                } else {
                    colorPicker.style.display = 'none';
                    colorPicker.style.visibility = 'hidden';
                    colorPicker.style.opacity = '0';
                }
            }
        }

        function toggleAnnotationFromPopout() {
            // Dispatch event to trigger annotation toggle in meetingActiveCtrl
            // This avoids duplicating the toggle logic that already exists there
            $rootScope.$broadcast('toggle-annotation-request', { fromPopout: true });
        }
    }
]);
